import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AppShellService } from '@core/services/app-shell.service';
import { BannerService } from '@core/services/banner.service';
import { FooterService } from '@core/services/footer.service';
import { LinkService } from '@core/services/link.service';
import { PortalDeterminationService } from '@core/services/portal-determination.service';
import { SessionKeepAliveService } from '@core/services/session-keep-alive.service';
import { TokenTimeoutService } from '@core/services/token/token-timeout.service';
import { TokenService } from '@core/services/token/token.service';
import { BrandingColors } from '@core/typings/branding.typing';
import { ClientSettingsService } from '@features/client-settings/client-settings.service';
import { MyInvitationsService } from '@features/my-invitations/my-invitations.service';
import { HelpContentService } from '@features/platform-admin/help-content/help-content.service';
import { InAppAlertService } from '@features/platform-admin/in-app-alerts/services/in-app-alerts.service';
import { UserService } from '@features/users/user.service';
import { SupportLink } from '@yourcause/common';
import { ButtonAction } from '@yourcause/common/buttons';
import { Subscription, tap } from 'rxjs';
import { Applicant } from '../../typings/applicant.typing';
import { ModalFactory } from '@yourcause/common/modals';

@Component({
  selector: 'gc-applicant-wrapper',
  templateUrl: './applicant-wrapper.component.html',
  styleUrls: ['./applicant-wrapper.component.scss']
})
export class ApplicantWrapperComponent implements OnInit, OnDestroy {
  loggedIn: boolean;
  sub = new Subscription();
  applicant: Applicant;
  mobileMenuOpen = false;
  hasModalOpen = false;
  copyrightText = this.footerService.getCopyrightText();
  termsOfServiceLink = this.linkService.getTermsOfServiceLink();
  privacyPolicyLink = this.linkService.getPrivacyPolicyLink();
  timeRemaining$ = this.timeoutService.timeRemaining$.pipe(
    tap(time => {
      if (time <= 0) {
        this.onSignOutClick();
      }
    }));
  showTimeout$ = this.timeoutService.showTimeout$;
  supportLinks$ = this.appShellService.changesTo$('supportLinks');
  helpActions: ButtonAction[] = [];
  links: ButtonAction[] = [
    {
      i18nDefault: 'My account',
      icon: 'user',
      i18nKey: 'common:hdrMyAccount',
      onClick: () => {
        this.router.navigate(['/apply/my-account']);
      }
    },
    {
      i18nDefault: 'Log out',
      icon: 'right-from-bracket',
      i18nKey: 'common:linkLogOut',
      onClick: () => {
        this.onSignOutClick();
      }
    }
  ];
  showAwardsLink = false;
  branding: BrandingColors;
  isBBGMApplicant = false;
  changes: MutationObserver;

  @HostListener('document:click', ['$event'])
  clickout (event: any) {
    if (
      event.target.className !== 'mobile-menu' &&
      event.target.className !== 'fa fa-bars'  &&
      this.mobileMenuOpen === true
    ) {
      this.mobileMenuOpen = false;
    }
  }

  constructor (
    private appShellService: AppShellService,
    private sessionKeepAliveService: SessionKeepAliveService,
    private timeoutService: TokenTimeoutService,
    private tokenService: TokenService,
    private router: Router,
    private portal: PortalDeterminationService,
    private userService: UserService,
    private bannerService: BannerService,
    private clientSettingsService: ClientSettingsService,
    private myInvitationsService: MyInvitationsService,
    private footerService: FooterService,
    private inAppAlertService: InAppAlertService,
    private linkService: LinkService,
    private helpContentService: HelpContentService,
    private modalFactory: ModalFactory
  ) {
    this.sub.add(
      this.clientSettingsService.changesTo$('clientBranding').subscribe(() => {
        this.branding = this.clientSettingsService.get('clientBranding') ||
          this.clientSettingsService.defaultBranding;
      })
    );
    this.sub.add(this.modalFactory.changesTo$('modalStorage').subscribe(() => {
      this.hasModalOpen = this.modalFactory.modalStorage?.length > 0;
    }));
  }

  get hasInvitations () {
    return this.myInvitationsService.myInvitations?.length > 0;
  }

  get banners () {
    return this.bannerService.banners;
  }

  async ngOnInit () {
    this.isBBGMApplicant = this.portal.isBBGMApplicant();
    this.handleLiveChatAvailabilityChanges();
    this.sub.add(
      this.userService.changesTo$('applicant').subscribe((applicant: Applicant) => {
        this.applicant = applicant;
        this.bannerService.setApplicantPortalBanners();
        this.showAwardsLink = this.applicant?.hasAwards;
        if (this.applicant && this.tokenService.hasCurrentValidToken()) {
          if (location.pathname.includes('auth')) {
            this.router.navigateByUrl('/apply/applications');
          }
          this.sessionKeepAliveService.startWatching();
          this.timeoutService.start();
          this.loggedIn = true;
        } else {
          this.sessionKeepAliveService.stopWatching();
          this.timeoutService.stop();
          this.loggedIn = false;
        }
      })
    );

    if (!!this.applicant) {
      this.setInvitations();
      this.handleInAppAlerts();
      this.setHelpContent();
    }
  }

  handleLiveChatAvailabilityChanges () {
    const chatWrapper = document.getElementById('embedded-messaging');
    if (!!chatWrapper) {
      this.changes = new MutationObserver((mutations: MutationRecord[]) => {
        mutations.forEach((mutation: MutationRecord) => {
          if (mutation.attributeName === 'style') {
            this.appShellService.setSupportLinks();
          }
        });
      });
      this.changes.observe(chatWrapper, { attributes: true, childList: true, subtree: true });
    }
  }

  async setInvitations () {
    await this.myInvitationsService.setMyInvitations();
  }

  async setHelpContent () {
    await this.helpContentService.getContents();
    this.appShellService.setSupportLinks();
    this.appShellService.registerSupportLinkListener();
  }

  async handleInAppAlerts () {
    await this.inAppAlertService.setInAppAlert();
    await this.inAppAlertService.handleOpenInAppAlertModal();
  }

  execSupportLink (link: SupportLink) {
    if ('onClick' in link) {
      link.onClick();
    } else if (link.external && link.link) {
      window.open(link.link);
    } else {
      this.router.navigate([link.link]);
    }
  }

  toggleMobileMenu () {
    this.mobileMenuOpen = !this.mobileMenuOpen;
  }

  async keepMeSignedIn () {
    await this.tokenService.getLatestToken();
  }

  onSignOutClick () {
    this.tokenService.logout();
  }

  ngOnDestroy () {
    this.sub.unsubscribe();
    this.changes?.disconnect();
  }
}
